// Faza 2 sledzenia pozycji: dopasowanie wektora chromy do szablonow akordow
// (48 = 12 rdzeni x 4 typy), korelacja kosinusowa. Czysta matematyka.

import type { Chroma } from './chromagram.ts';

export interface DetectedChord {
  /** np. "Am", "C", "G". */
  name: string;
  /** 0..1, korelacja kosinusowa z najlepszym szablonem. */
  confidence: number;
}

/** Prog ponizej ktorego nie zglaszamy zadnego dopasowania. */
const PROG_PEWNOSCI = 0.6;

/** Nazwy rdzeni wg indeksu klasy wysokosci, C=0. */
const NAZWY_RDZENI = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

interface SzablonTypu {
  /** Sufiks nazwy akordu, pusty dla durowego. */
  sufiks: string;
  /** Binarny profil klas wysokosci przy rdzeniu = 0 (C). */
  profil: readonly number[];
}

const TYPY: readonly SzablonTypu[] = [
  { sufiks: '', profil: [1, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0] }, // dur
  { sufiks: 'm', profil: [1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0] }, // mol
  { sufiks: 'maj7', profil: [1, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1] }, // dur7 (maj7)
  { sufiks: 'm7', profil: [1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0] }, // mol7
];

interface Szablon {
  name: string;
  wektor: readonly number[];
}

/** Obraca profil o `n` polton (w prawo, tj. rdzen profilu ląduje na klasie `n`). */
function obrocProfil(profil: readonly number[], n: number): number[] {
  const wynik = new Array<number>(12).fill(0);
  for (let i = 0; i < 12; i++) {
    wynik[(i + n) % 12] = profil[i] ?? 0;
  }
  return wynik;
}

function zbudujSzablony(): Szablon[] {
  const szablony: Szablon[] = [];
  for (let rdzen = 0; rdzen < 12; rdzen++) {
    for (const typ of TYPY) {
      szablony.push({
        name: `${NAZWY_RDZENI[rdzen]}${typ.sufiks}`,
        wektor: obrocProfil(typ.profil, rdzen),
      });
    }
  }
  return szablony;
}

const SZABLONY = zbudujSzablony();

function podobienstwoKosinusowe(a: Chroma, b: readonly number[]): number {
  let iloczyn = 0;
  let normaA = 0;
  let normaB = 0;
  for (let i = 0; i < 12; i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    iloczyn += av * bv;
    normaA += av * av;
    normaB += bv * bv;
  }
  if (normaA === 0 || normaB === 0) return 0;
  return iloczyn / (Math.sqrt(normaA) * Math.sqrt(normaB));
}

/**
 * Rozpoznaje akord z wektora chromy przez porownanie z 48 szablonami
 * (korelacja kosinusowa). Zwraca najlepsze dopasowanie, albo `null`, gdy
 * pewnosc jest ponizej progu (0.6).
 */
export function detectChord(chroma: Chroma): DetectedChord | null {
  let najlepszy: Szablon | null = null;
  let najlepszePodobienstwo = -1;

  for (const szablon of SZABLONY) {
    const podobienstwo = podobienstwoKosinusowe(chroma, szablon.wektor);
    if (podobienstwo > najlepszePodobienstwo) {
      najlepszePodobienstwo = podobienstwo;
      najlepszy = szablon;
    }
  }

  if (!najlepszy || najlepszePodobienstwo < PROG_PEWNOSCI) return null;

  return { name: najlepszy.name, confidence: najlepszePodobienstwo };
}
